import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { IAccount } from '~/types/application-management'

import Button from '~/components/Buttons/Button'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import { FORM_ERRORS } from '~/const'
import { keys as appsManagementKeys, useUpdateAccountMutation } from '~/queries/application-management'
import { displayToast } from '~/utils/toast'

import './styles.scss'

export type TSection = 'general' | 'technical' | 'other' | 'store_ios' | 'store_android' | 'store_hms' | 'store_web'

const validationSchema = Yup.object().shape({
  account_key: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
  app_store_id: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
  app_store_subscription_key: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
  play_store_id: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
})

interface FormProps {
  account: IAccount
  onClose: VoidFunction
}

const UpdateAccountForm = ({ account, onClose }: FormProps): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
  } = useForm<IAccount>({
    defaultValues: account,
    resolver: yupResolver(validationSchema),
  })

  /** Queries & Mutations */

  const { mutateAsync: updateAccount } = useUpdateAccountMutation()

  /** Handlers */

  const handleFormSubmit = async (values: IAccount) => {
    try {
      await updateAccount(values)
      await queryClient.invalidateQueries({ queryKey: appsManagementKeys.account(values.account_key) })
      displayToast('Account successfully updated', 'success')
      onClose()
    } catch {
      displayToast('Account could not be updated', 'error')
    }
  }

  /** Render */

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody className="update-account-modal-body">
        <TextInput
          {...register('account_key')}
          error={errors.account_key?.message}
          label="Account key"
          name="account_key"
          placeholder="Edit the account key"
          required
        />
        <TextInput
          {...register('app_store_id')}
          error={errors.app_store_id?.message}
          label="AppStore Account ID"
          name="app_store_id"
          placeholder="Edit the AppStore Account ID"
          required
        />
        <TextInput
          {...register('app_store_subscription_key')}
          error={errors.app_store_subscription_key?.message}
          label="App Store Subscription Key"
          placeholder="Edit the App Store subscription key"
          required
        />
        <TextInput
          {...register('play_store_id')}
          error={errors.play_store_id?.message}
          label="Play Store ID"
          placeholder="Edit the Play Store ID"
          required
        />
      </ModalBody>
      <ModalFooter>
        <Button ghost onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          Save
        </Button>
      </ModalFooter>
    </form>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
}

const UpdateAccountModal = ({ account, isOpen, onClose }: ModalProps): ReactElement => {
  return (
    <Modal className="update-account-modal" isOpen={isOpen} onCloseModal={onClose} shouldCloseOnOverlayClick={false}>
      <ModalHeader>Update Account</ModalHeader>
      <UpdateAccountForm account={account} onClose={onClose} />
    </Modal>
  )
}

export default UpdateAccountModal
